import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { CreditFacilityDto } from './use-get-credit-facilities-api';

const endpoint = 'ums/v1/cms/bnpl/credit-facilities';
export const getCreditFacilityEndpointIdentifier = endpoint;

export interface CreditEventDto {
  id: number;
  facilityId: number;
  eventType: string;
  eventAmount: number;
  balanceUtilized: number;
  balanceReserved: number;
  balanceAvailable: number;
  referenceType: string | null;
  referenceId: number | null;
  description: string | null;
  createdBy: number;
  createdAt: string;
}

interface CreditFacilityDetailDto extends CreditFacilityDto {
  creditEvents: CreditEventDto[];
}

interface ResponseData {
  data: CreditFacilityDetailDto;
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [string, number | string];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetCreditFacilityApi(facilityId: number | string, options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();

  const queryFn = async () => {
    try {
      const { data } = await http.get(`${endpoint}/${facilityId}`);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useQueryWrapper([`${endpoint}/detail`, facilityId], queryFn, {
    enabled: isLoggedIn && !!facilityId,
    ...options,
  });
}
